import { atom } from 'jotai';

export interface ReportTracker {
  /**
   * Optional sonner toast id. Present when a "Generating PDF report…" loading
   * toast was shown at start time (standalone "Generate PDF report" button) —
   * useReportProgress then UPDATES that same toast into a success/error toast.
   * Absent when triggered from the deviation modal (the deviation toast
   * already conveys progress), in which case useReportProgress creates a
   * NEW toast on completion.
   */
  toastId?: string | number;
  batchId: string;
  projectId: string;
  title: string;
  /**
   * How many station pages the report will have (the dock's `binCount`).
   * Absent for a deviation or station-volumes summary report. Carried for the
   * progress copy; the render stage quotes `renderCount`, not this.
   */
  pageCount?: number;
  /**
   * How many top-down station renders the RENDER job will draw — every bin in
   * the window, blank ones included, so it is >= `pageCount`. Set ONLY by
   * `GenerateReportDialog`, and only when the user ticked the render box, so
   * it doubles as the flag that tells `useReportProgress` this tracker may
   * follow a RENDER job: a deviation or surface-difference run puts its own
   * render child in the same batch as its chained report, and those trackers
   * must ignore it.
   */
  renderCount?: number;
  /**
   * Which job the tracker is currently waiting on. A per-station PDF with
   * renders (spec §13.7) is TWO jobs: a RENDER child that chains the
   * PDF_REPORT job when it finishes, so the report job does not exist for the
   * first minutes of the wait. `'rendering'` marks that the loading toast has
   * already been switched to the render stage, so the hook does not re-issue
   * it on every 2 s poll; `'pdf'` marks that the handover to the report job
   * has been made (and any non-fatal render warning already shown).
   */
  stage?: 'rendering' | 'pdf';
  /**
   * Compare surfaces only (R9): when the comparison toast settled with a
   * success, the combined PDF is queued server-side by whichever part finished
   * last — usually at once, but a lost claim (spec §12.11) queues nothing. Past
   * this epoch-ms deadline a tracker that still sees no PDF_REPORT row in its
   * batch is dropped instead of polling for ever. Absent on every other tracker.
   */
  startDeadlineAt?: number;
}

export const reportTrackingAtom = atom<ReportTracker[]>([]);

/** How long a settled comparison waits for its combined PDF's job to appear (R9). */
export const COMPARISON_REPORT_START_TIMEOUT_MS = 10 * 60 * 1000;

/** `trackers` with a start deadline on the one(s) following `batchId`; one already set is kept. */
export const withReportDeadline = (trackers: ReportTracker[], batchId: string, deadlineAt: number): ReportTracker[] =>
  trackers.map((tr) => (tr.batchId === batchId && tr.startDeadlineAt == null ? { ...tr, startDeadlineAt: deadlineAt } : tr));

/** `trackers` without the one following `batchId` (a paired run whose every part failed queues no PDF). */
export const withoutReportTracker = (trackers: ReportTracker[], batchId: string): ReportTracker[] =>
  trackers.filter((tr) => tr.batchId !== batchId);

/**
 * `trackers` with the one following `from` moved to `to`: a paired run's
 * report-batch part failed, so the server queues the combined PDF on the
 * surviving part's batch (`comparisonReport.ts`, `reportRoot`).
 */
export const withReportBatchMoved = (trackers: ReportTracker[], from: string, to: string): ReportTracker[] =>
  trackers.map((tr) => (tr.batchId === from ? { ...tr, batchId: to } : tr));
